export interface Props {
  trade?: string;
  entity?: string;
  onRevaluationComplete?: () => void;
}

export type RevalueTradesMetricsRequestPayload = {
  start_date: string;
  end_date: string;
  trades?: number[];
  company_entity_id?: string;
  hedging_instruments?: number[];
};

export type RevalueTradesMetricsSuccessPayload = {
  task_id?: string;
};

export type TaskProgressSuccessPayload = {
  current_count: number;
  total_count: number;
  status: string;
  state: string;
  statistic: {
    created_at: string;
    finished_at: string;
    duration: string;
    error?: string;
  };
};

export interface ReportDownloadSuccessPayload {
  download_url: string;
}

/** Run type discriminator returned by (and accepted as a filter for) the task listing endpoint. */
export enum RevalueType {
  VALUE_AND_METRICS = 'VALUE_AND_METRICS',
  BCVA = 'BCVA',
}

export type ListTasksParams = {
  /** Only in-flight runs (queued/created/running). Defaults to true on the backend. */
  active?: boolean;
  revalue_type?: RevalueType;
  limit?: number;
};

export type CancelAllTasksParams = {
  /** Scope the cancel to one run type. Omit to cancel all of the user's runs. */
  revalue_type?: RevalueType;
};

export type CancelAllTasksResult = {
  cancelled_count: number;
  cancelled_task_ids: string[];
};

/** Per-report outcome of a deliverable run, e.g. `{ REGRESSION: 'success' }` or an error message. */
export type DeliverableReportResult = Record<string, string>;

/**
 * One entry from `GET /v2/admin/tasks/`.
 *
 * The endpoint serves every producer that writes to the shared task table, so a task is either a
 * revaluation run (`revalue_type` set) or a deliverable run (`run_params.task_kind === 'DELIVERABLE'`).
 * Fields belonging to the other producer are simply absent — read them through the helpers in
 * `store/backgroundTasks/constants` rather than testing shapes at each call site.
 */
export type BackgroundTask = {
  task_id: string;
  status: string;
  current_count: number;
  total_count: number;
  state: string;
  /** Null for producers other than revaluation. */
  revalue_type: RevalueType | string | null;
  run_params: {
    start_date?: string | null;
    end_date?: string | null;
    trades?: number[] | null;
    company_entity_id?: string | null;
    hedging_instruments?: number[] | null;
    /** Present on deliverable runs only. */
    task_kind?: string;
    job_kind?: string;
    entity_name?: string | null;
    run_date?: string;
    report_types?: number[];
    schedule_id?: number | null;
  };
  statistic: {
    created_at: string;
    finished_at?: string;
    duration: string;
    error?: string;
    /** Set once a producer has published a downloadable result to S3. */
    s3_key?: string;
    file_name?: string;
    build_report_results?: DeliverableReportResult[];
  };
  created_at: string;
  updated_at: string;
};
